/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ResponsiveRadialBar } from '@nivo/radial-bar';
import { Text } from '@nivo/text';
import { BasicTooltip } from '@nivo/tooltip';
import { animated, to } from '@react-spring/web';

import { AdviceSeverity, AdviceSeverityColor, AdviceType } from 'shared';

const MAX_VALUE_BOUNDARY = 10; // The lowest number where nivo does not show decimal values is 8, but I fear that might confuse users

type Props = { advice: { type: AdviceType; severity: AdviceSeverity }[] };

// Roundabout way of getting i18n for the label, since I wasn't able to find any other way
// Taken from https://github.com/plouc/nivo/blob/a2d9dab855365926cb41267eb20af154ca8fd558/packages/polar-axes/src/RadialAxisTick.tsx#L5-L30
const LabelComponent = memo((props: any) => {
  const { t } = useTranslation();

  return (
    <animated.g
      opacity={props.animated.opacity}
      transform={to(
        [props.animated.y, props.animated.rotation],
        (y, rotation) => `translate(${y}, 0) rotate(${rotation})`,
      )}
    >
      <animated.line x2={props.animated.length} style={props.theme.ticks.line} />
      <Text
        dx={props.animated.textX}
        textAnchor={props.textAnchor}
        dominantBaseline="central"
        style={props.theme.ticks.text}
      >
        {t(`cluster:advice.modules.${props.label}`)}
      </Text>
    </animated.g>
  );
});

export const AdviceChart = memo<Props>(({ advice }) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const adv = advice.reduce(
      (acc, cur) => {
        acc[cur.type] ??= { id: cur.type, data: {} };
        acc[cur.type]!.data[cur.severity] ??= { x: cur.severity, y: 0 };
        acc[cur.type]!.data[cur.severity]!.y += 1;
        return acc;
      },
      {} as Partial<
        Record<AdviceType, { id: AdviceType; data: Partial<Record<AdviceSeverity, { x: AdviceSeverity; y: number }>> }>
      >,
    );

    return (
      Object.values(adv)
        .map(({ id, data }) => ({ id, data: Object.values(data) }))
        // Sort so the types with more advice are on the outer rings
        .sort((a, b) => a.data.reduce((acc, cur) => acc + cur.y, 0) - b.data.reduce((acc, cur) => acc + cur.y, 0))
    );
  }, [advice]);

  const maxValue = useMemo(
    () =>
      Math.max(
        chartData.reduce(
          (max, cur) =>
            Math.max(
              max,
              cur.data.reduce((acc, cur) => acc + cur.y, 0),
            ),
          0,
        ),
        MAX_VALUE_BOUNDARY,
      ),
    [chartData],
  );

  return (
    <ResponsiveRadialBar
      data={chartData}
      innerRadius={0.2}
      padding={0.25}
      cornerRadius={2}
      margin={{ top: 40, bottom: 40 }}
      enableTracks={false}
      enableCircularGrid={false}
      maxValue={maxValue}
      radialAxisStart={{
        tickSize: 16,
        tickPadding: 4,
        tickRotation: 0,
        style: { ticks: { text: { fontSize: 12 } } },
        tickComponent: LabelComponent,
      }}
      circularAxisOuter={{ tickSize: 5, tickPadding: 12, tickRotation: 0 }}
      colors={datum => AdviceSeverityColor[datum.category as AdviceSeverity]}
      animate={true}
      motionConfig="gentle"
      tooltip={({ bar }) => (
        <BasicTooltip
          enableChip
          id={t(`cluster:advice.severities.${bar.category}`)}
          value={bar.formattedValue}
          color={bar.color}
        />
      )}
    />
  );
});
